// Síntese de Voz — OmniVoice (k2-fsa), stack omnivoice-tts (ROCm, RX6800).
// Substituiu o Qwen. Modo principal: voz automática (texto→áudio). Clonagem
// opcional via referência. Sem "speakers" fixos — a voz é gerada pelo modelo.
// Controles de tempo: velocidade (speed <1.0 = mais devagar) e duração fixa (s,
// sobrepõe a velocidade). Vozes clonadas podem ser salvas (referência + transcrição).
import { useState, useEffect, useRef, useCallback } from 'react'
import { FiMic, FiPlay, FiLoader, FiDownload, FiRefreshCw, FiChevronDown, FiChevronRight, FiUploadCloud, FiTrash2, FiCopy, FiZap, FiRotateCcw, FiSliders, FiSave, FiBookmark, FiEdit2, FiCheck, FiX } from 'react-icons/fi'
import { API_ENDPOINTS } from '../lib/api'

interface OmniVoiceLabProps {
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
}

interface SavedVoice { filename: string; label: string; ref_text: string; saved_at?: string }

const DEFAULT_TEXT = 'Olá! Esta é a voz do OmniVoice rodando localmente no servidor TWMS, em português do Brasil.'

export function OmniVoiceLab({ authenticatedFetch }: OmniVoiceLabProps) {
  const [info, setInfo] = useState<any | null>(null)
  const [infoError, setInfoError] = useState<string | null>(null)

  const [text, setText] = useState(DEFAULT_TEXT)
  const [numStep, setNumStep] = useState(32)
  const [speed, setSpeed] = useState(1.0)
  const [duration, setDuration] = useState('')   // vazio = usar velocidade
  const [instruct, setInstruct] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)

  const [normalizing, setNormalizing] = useState(false)
  const [preNorm, setPreNorm] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState<number | null>(null)
  const lastUrl = useRef<string | null>(null)

  // Vozes salvas
  const [savedVoices, setSavedVoices] = useState<SavedVoice[]>([])
  const [showVoices, setShowVoices] = useState(true)
  const [selectedVoice, setSelectedVoice] = useState('') // '' = automática; senão filename da voz salva
  const [voiceBusy, setVoiceBusy] = useState<string | null>(null) // filename em exclusão
  const [renamingFile, setRenamingFile] = useState<string | null>(null) // filename em renomeação
  const [renameLabel, setRenameLabel] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)

  // Clonagem
  const [showClone, setShowClone] = useState(false)
  const [cloneFile, setCloneFile] = useState<File | null>(null)
  const [cloneStaged, setCloneStaged] = useState<string | null>(null)
  const [cloneRefText, setCloneRefText] = useState('')
  const [cloneBusy, setCloneBusy] = useState<null | 'upload' | 'test' | 'discard' | 'save'>(null)
  const [cloneMsg, setCloneMsg] = useState<string | null>(null)
  const [cloneError, setCloneError] = useState<string | null>(null)
  const [cloneAudioUrl, setCloneAudioUrl] = useState<string | null>(null)
  const [saveLabel, setSaveLabel] = useState('')
  const [savedThisOne, setSavedThisOne] = useState(false)
  const cloneAudioRef = useRef<string | null>(null)

  // API pública (tts.twms.com.br) — doc + chave Bearer
  const [showApi, setShowApi] = useState(false)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [apiKeyError, setApiKeyError] = useState<string | null>(null)
  const [revealKey, setRevealKey] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [copied, setCopied] = useState(false)

  // duração parseada (>0) sobrepõe velocidade no backend; senão manda só speed
  const durationVal = (() => { const n = Number(duration); return duration.trim() && n > 0 ? n : null })()
  const timing = () => ({ speed, duration: durationVal })

  const loadInfo = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_INFO)
      const data = await r.json().catch(() => ({}))
      if (!data.ok) throw new Error(data.error || 'OmniVoice indisponível')
      setInfo(data.info)
      setInfoError(null)
    } catch (e) {
      setInfoError(e instanceof Error ? e.message : 'Falha ao consultar o serviço OmniVoice')
    }
  }, [authenticatedFetch])

  const loadVoices = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_VOICES)
      const data = await r.json().catch(() => ({}))
      if (data.ok && Array.isArray(data.voices)) setSavedVoices(data.voices)
    } catch { /* silencioso — seção some se não houver vozes */ }
  }, [authenticatedFetch])

  const loadKey = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_KEY)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setApiKey(data.token); setApiKeyError(null)
    } catch (e) {
      setApiKeyError(e instanceof Error ? e.message : 'Falha ao ler a chave')
    }
  }, [authenticatedFetch])

  const rotateKey = async () => {
    if (rotating) return
    if (!window.confirm('Gerar uma nova chave Bearer? A chave atual deixa de funcionar imediatamente — quem consome a API precisa ser atualizado.')) return
    setRotating(true); setApiKeyError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_KEY_ROTATE, { method: 'POST' })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setApiKey(data.token); setRevealKey(true)
    } catch (e) {
      setApiKeyError(e instanceof Error ? e.message : 'Falha ao gerar nova chave')
    } finally { setRotating(false) }
  }

  const copyKey = () => {
    if (!apiKey) return
    navigator.clipboard?.writeText(apiKey).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) }).catch(() => {})
  }

  useEffect(() => { loadInfo(); loadVoices(); loadKey() }, [loadInfo, loadVoices, loadKey])
  useEffect(() => () => {
    if (lastUrl.current) URL.revokeObjectURL(lastUrl.current)
    if (cloneAudioRef.current) URL.revokeObjectURL(cloneAudioRef.current)
  }, [])

  const normalizeText = async () => {
    const t = text.trim()
    if (!t || normalizing) return
    setError(null); setNormalizing(true)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.TTS_NORMALIZE, { method: 'POST', body: JSON.stringify({ text: t }), timeout: 60000 } as RequestInit)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setPreNorm(t); setText(data.normalized)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao normalizar')
    } finally { setNormalizing(false) }
  }
  const undoNormalize = () => { if (preNorm != null) { setText(preNorm); setPreNorm(null) } }

  const setMainAudio = (blobUrl: string) => {
    if (lastUrl.current) URL.revokeObjectURL(lastUrl.current)
    lastUrl.current = blobUrl; setAudioUrl(blobUrl)
  }

  // "Gerar áudio": usa a voz selecionada (clone com referência salva) ou,
  // se "Automática", o modo voz automática / voice design.
  const generate = async () => {
    const t = text.trim()
    if (!t || loading) return
    setError(null); setLoading(true); setElapsed(null)
    const t0 = Date.now()
    try {
      let r: Response
      if (selectedVoice) {
        const v = savedVoices.find(x => x.filename === selectedVoice)
        r = await authenticatedFetch(API_ENDPOINTS.OTTS_CLONE, {
          method: 'POST',
          body: JSON.stringify({ text: t, reference_audio_filename: selectedVoice, ref_text: v?.ref_text || null, instruct: instruct.trim() || null, num_step: numStep, ...timing() }),
          timeout: 300000,
        } as RequestInit)
      } else {
        r = await authenticatedFetch(API_ENDPOINTS.OTTS_GENERATE, {
          method: 'POST', body: JSON.stringify({ text: t, num_step: numStep, instruct: instruct.trim() || null, ...timing() }), timeout: 300000,
        } as RequestInit)
      }
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
      setMainAudio(URL.createObjectURL(await r.blob())); setElapsed((Date.now() - t0) / 1000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao gerar áudio')
    } finally { setLoading(false) }
  }

  const deleteVoice = async (v: SavedVoice) => {
    if (voiceBusy) return
    setError(null); setVoiceBusy(v.filename)
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.OTTS_CLONE_DISCARD}/${encodeURIComponent(v.filename)}`, { method: 'DELETE' })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      if (selectedVoice === v.filename) setSelectedVoice('')
      await loadVoices()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao excluir voz')
    } finally { setVoiceBusy(null) }
  }

  const startRename = (v: SavedVoice) => { setRenamingFile(v.filename); setRenameLabel(v.label) }
  const cancelRename = () => { setRenamingFile(null); setRenameLabel('') }

  // Renomear = atualizar só o label (display); reusa /voices/save preservando o ref_text.
  const submitRename = async () => {
    const v = savedVoices.find(x => x.filename === renamingFile)
    if (!v || renameBusy) return
    const label = renameLabel.trim()
    if (!label || label === v.label) { cancelRename(); return }
    setRenameBusy(true); setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_VOICE_SAVE, {
        method: 'POST',
        body: JSON.stringify({ filename: v.filename, label, ref_text: v.ref_text || '' }),
      } as RequestInit)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      cancelRename(); await loadVoices()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao renomear voz')
    } finally { setRenameBusy(false) }
  }

  const uploadClone = async () => {
    if (!cloneFile || cloneBusy) return
    setCloneError(null); setCloneMsg(null); setSavedThisOne(false); setCloneBusy('upload')
    try {
      const fd = new FormData(); fd.append('audio', cloneFile)
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_CLONE_UPLOAD, { method: 'POST', body: fd, timeout: 60000 } as RequestInit)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setCloneStaged(data.filename); setSaveLabel(data.filename.replace(/\.[^.]+$/, ''))
      setCloneMsg(`Referência "${data.filename}" enviada. Informe a transcrição (recomendado), teste e salve a voz.`)
    } catch (e) {
      setCloneError(e instanceof Error ? e.message : 'Falha ao enviar amostra')
    } finally { setCloneBusy(null) }
  }

  const testClone = async () => {
    if (!cloneStaged || cloneBusy) return
    const t = text.trim()
    if (!t) { setCloneError('Escreva o texto a falar no campo acima.'); return }
    setCloneError(null); setCloneBusy('test')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_CLONE, {
        method: 'POST',
        body: JSON.stringify({ text: t, reference_audio_filename: cloneStaged, ref_text: cloneRefText.trim() || null, instruct: instruct.trim() || null, num_step: numStep, ...timing() }),
        timeout: 300000,
      } as RequestInit)
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
      if (cloneAudioRef.current) URL.revokeObjectURL(cloneAudioRef.current)
      const url = URL.createObjectURL(await r.blob())
      cloneAudioRef.current = url; setCloneAudioUrl(url)
    } catch (e) {
      setCloneError(e instanceof Error ? e.message : 'Falha ao testar clonagem')
    } finally { setCloneBusy(null) }
  }

  const saveVoice = async () => {
    if (!cloneStaged || cloneBusy) return
    const label = saveLabel.trim() || cloneStaged
    setCloneError(null); setCloneBusy('save')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.OTTS_VOICE_SAVE, {
        method: 'POST',
        body: JSON.stringify({ filename: cloneStaged, label, ref_text: cloneRefText.trim() || '' }),
      } as RequestInit)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setSavedThisOne(true); setCloneMsg(`Voz "${label}" salva — já selecionada no gerador (seletor "Voz").`)
      await loadVoices(); setShowVoices(true); if (cloneStaged) setSelectedVoice(cloneStaged)
    } catch (e) {
      setCloneError(e instanceof Error ? e.message : 'Falha ao salvar voz')
    } finally { setCloneBusy(null) }
  }

  const resetClone = () => {
    if (cloneAudioRef.current) { URL.revokeObjectURL(cloneAudioRef.current); cloneAudioRef.current = null }
    setCloneFile(null); setCloneStaged(null); setCloneAudioUrl(null); setCloneRefText(''); setSaveLabel(''); setSavedThisOne(false)
  }

  // Descartar: só apaga a referência do servidor se ela NÃO foi salva (senão vira exclusão da voz salva).
  const discardClone = async () => {
    if (cloneBusy) return
    setCloneError(null)
    if (!cloneStaged || savedThisOne) { resetClone(); return }
    setCloneBusy('discard')
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.OTTS_CLONE_DISCARD}/${encodeURIComponent(cloneStaged)}`, { method: 'DELETE' })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setCloneMsg('Referência descartada.'); resetClone()
    } catch (e) {
      setCloneError(e instanceof Error ? e.message : 'Falha ao descartar')
    } finally { setCloneBusy(null) }
  }

  const serviceUp = !!info && !infoError
  const busy = loading || !!voiceBusy

  return (
    <div className="w-full max-w-5xl mx-auto flex flex-col gap-3 p-1">
      <div className="flex items-center gap-2">
        <FiMic className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-bold text-foreground">Síntese de Voz — OmniVoice</h2>
        <span className="text-[11px] text-muted-foreground">OmniVoice · local (RX6800)</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${serviceUp ? 'bg-emerald-500' : 'bg-red-500'}`} />
          <span className="text-[11px] text-muted-foreground">{serviceUp ? (info?.loaded ? 'residente' : 'ocioso') : 'offline'}</span>
          <button onClick={loadInfo} title="Atualizar" className="p-1 rounded hover:bg-muted transition-colors"><FiRefreshCw className="w-3 h-3 text-muted-foreground" /></button>
        </span>
      </div>

      {infoError && <div className="px-2.5 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-700 dark:text-red-300">Serviço OmniVoice inacessível: {infoError}</div>}

      {/* Layout em 2 colunas (lg+): esquerda = geração, direita = vozes/clonagem/API. Abaixo de lg empilha. Evita scroll. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
      {/* ── Coluna esquerda: geração ── */}
      <div className="flex flex-col gap-3 min-w-0">

      <textarea value={text} onChange={e => { setText(e.target.value); if (preNorm != null) setPreNorm(null) }} rows={4}
        placeholder="Escreva o texto a ser falado…" disabled={loading}
        className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
      <div className="flex items-center gap-2 -mt-1">
        <button onClick={normalizeText} disabled={normalizing || loading || !text.trim()}
          title="Reescreve números, datas, valores e abreviações por extenso em pt-BR"
          className="inline-flex items-center gap-1.5 text-[11px] text-primary hover:underline disabled:opacity-40">
          {normalizing ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiZap className="w-3 h-3" />} Normalizar pt-BR (IA)
        </button>
        {preNorm != null && <button onClick={undoNormalize} disabled={loading} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"><FiRotateCcw className="w-3 h-3" /> desfazer</button>}
      </div>

      {/* Controles de voz (valem para gerar, clonar e vozes salvas) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-card border border-border neu-card rounded-lg p-2.5">
        <label className="flex items-center gap-1.5 text-[11px] text-foreground">
          <span className="text-muted-foreground">voz</span>
          <select value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)} disabled={busy}
            title="Automática usa voz gerada pelo modelo (+ voice design); uma voz salva gera por clonagem."
            className="text-[11px] rounded border border-border bg-background px-1.5 py-0.5 max-w-[180px] focus:outline-none">
            <option value="">Automática</option>
            {savedVoices.map(v => <option key={v.filename} value={v.filename}>{v.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[11px] text-foreground">
          <span className="text-muted-foreground">velocidade</span>
          <input type="range" min={0.7} max={1.3} step={0.05} value={speed} disabled={!!durationVal || busy}
            onChange={e => setSpeed(Number(e.target.value))} className="w-32 accent-primary disabled:opacity-40"
            title="Abaixo de 1.0 deixa a fala mais lenta; acima, mais rápida. Ignorado se a duração fixa estiver preenchida." />
          <span className="font-mono tabular-nums w-10">{speed.toFixed(2)}×</span>
          {speed !== 1.0 && <button onClick={() => setSpeed(1.0)} disabled={busy} title="Voltar a 1.0×" className="text-muted-foreground hover:text-foreground"><FiRotateCcw className="w-3 h-3" /></button>}
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground" title="Duração fixa do áudio em segundos. Quando preenchida, SOBREPÕE a velocidade.">
          duração fixa
          <input type="number" min={0} step={0.5} value={duration} onChange={e => setDuration(e.target.value)} disabled={busy}
            placeholder="auto" className="w-16 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary" />
          <span>s</span>
        </label>
        <label className="flex items-center gap-1 text-[11px] text-muted-foreground ml-auto">
          qualidade
          <select value={numStep} onChange={e => setNumStep(Number(e.target.value))} disabled={busy}
            className="text-[11px] rounded border border-border bg-background px-1 py-0.5 focus:outline-none">
            <option value={16}>rápida (16)</option>
            <option value={32}>alta (32)</option>
          </select>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={generate} disabled={loading || !text.trim() || !serviceUp}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground neu-relief px-4 py-2 text-sm font-medium disabled:opacity-40">
          {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlay className="w-4 h-4" />}{loading ? 'Gerando…' : 'Gerar áudio'}
        </button>
        {durationVal && <span className="text-[11px] text-muted-foreground">duração fixa {durationVal}s (ignora velocidade)</span>}
        {busy && !durationVal && <span className="text-[11px] text-muted-foreground">~1,8× tempo real na RX6800</span>}
        {elapsed != null && !busy && <span className="text-[11px] text-muted-foreground tabular-nums">gerado em {elapsed.toFixed(1)}s</span>}
      </div>

      {error && <div className="text-[11px] text-red-600 dark:text-red-400">{error}</div>}

      {audioUrl && (
        <div className="flex items-center gap-2 bg-card border border-border neu-card rounded-lg p-2">
          <audio controls autoPlay src={audioUrl} className="flex-1 h-9" />
          <a href={audioUrl} download="omnivoice.wav" title="Baixar WAV" className="p-2 rounded hover:bg-muted transition-colors"><FiDownload className="w-4 h-4 text-muted-foreground" /></a>
        </div>
      )}

      </div>{/* ── fim coluna esquerda ── */}

      {/* ── Coluna direita: vozes salvas, voice design, clonagem, API ── */}
      <div className="flex flex-col gap-3 min-w-0">

      {/* Vozes salvas */}
      {savedVoices.length > 0 && (
        <div className="border border-border neu-card rounded-lg overflow-hidden">
          <button onClick={() => setShowVoices(s => !s)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted/60 transition-colors">
            {showVoices ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
            <FiBookmark className="w-3.5 h-3.5 text-primary" /> Vozes salvas <span className="text-muted-foreground font-normal">({savedVoices.length})</span>
          </button>
          {showVoices && (
            <div className="px-3 pb-3 flex flex-col gap-2">
              <p className="text-[11px] text-muted-foreground">Para gerar com uma voz salva, escolha-a no seletor <span className="font-medium text-foreground">"voz"</span> acima e clique em "Gerar áudio".</p>
              {savedVoices.map(v => (
                <div key={v.filename} className={`flex items-center gap-2 bg-card neu-item border rounded-lg p-2 ${selectedVoice === v.filename ? 'border-primary' : 'border-border'}`}>
                  {renamingFile === v.filename ? (
                    <>
                      <input value={renameLabel} onChange={e => setRenameLabel(e.target.value)} disabled={renameBusy} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') cancelRename() }}
                        placeholder="Novo nome da voz"
                        className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
                      <button onClick={submitRename} disabled={renameBusy || !renameLabel.trim()} title="Salvar nome"
                        className="p-2 rounded hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 disabled:opacity-40">
                        {renameBusy ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={cancelRename} disabled={renameBusy} title="Cancelar"
                        className="p-2 rounded hover:bg-muted text-muted-foreground disabled:opacity-40"><FiX className="w-3.5 h-3.5" /></button>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-medium text-foreground truncate">{v.label}{selectedVoice === v.filename && <span className="ml-1.5 text-[10px] text-primary">● selecionada</span>}</div>
                        {v.ref_text && <div className="text-[10px] text-muted-foreground truncate" title={v.ref_text}>“{v.ref_text}”</div>}
                      </div>
                      <button onClick={() => startRename(v)} disabled={busy} title="Renomear voz"
                        className="p-2 rounded hover:bg-muted text-muted-foreground disabled:opacity-40"><FiEdit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setSelectedVoice(v.filename)} disabled={busy || selectedVoice === v.filename}
                        title="Selecionar esta voz no gerador"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted disabled:opacity-40">
                        <FiBookmark className="w-3.5 h-3.5" /> Usar
                      </button>
                      <button onClick={() => deleteVoice(v)} disabled={busy} title="Excluir voz salva"
                        className="p-2 rounded hover:bg-red-500/10 text-red-600 dark:text-red-400 disabled:opacity-40">
                        {voiceBusy === v.filename ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiTrash2 className="w-3.5 h-3.5" />}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Voice design (avançado, opcional) */}
      <div className="border border-border neu-card rounded-lg overflow-hidden">
        <button onClick={() => setShowAdvanced(s => !s)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted/60 transition-colors">
          {showAdvanced ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
          <FiSliders className="w-3.5 h-3.5 text-primary" /> Voice design (opcional)
        </button>
        {showAdvanced && (
          <div className="px-3 pb-3 flex flex-col gap-2">
            <p className="text-[11px] text-muted-foreground">
              Descreva a voz desejada (gênero, idade, tom). Em inglês funciona melhor (o design é treinado em EN/ZH).
              Deixe vazio para voz automática. Ex.: <span className="font-mono">female, low pitch</span>.
              <br />Com uma voz salva/clonada selecionada, isto <span className="font-medium text-foreground">reforça</span> a clonagem:
              atributos consistentes com a referência ganham estabilidade; conflitantes são ignorados (a referência prevalece).
            </p>
            <input value={instruct} onChange={e => setInstruct(e.target.value)} disabled={loading}
              placeholder="female, low pitch, calm  (vazio = voz automática)"
              className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        )}
      </div>

      {/* Clonagem */}
      <div className="border border-border neu-card rounded-lg overflow-hidden">
        <button onClick={() => setShowClone(s => !s)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted/60 transition-colors">
          {showClone ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
          <FiCopy className="w-3.5 h-3.5 text-primary" /> Clonagem de voz
        </button>
        {showClone && (
          <div className="px-3 pb-3 flex flex-col gap-2.5">
            <p className="text-[11px] text-muted-foreground">
              Envie 3-10s de áudio limpo (.wav/.mp3) — fale pausado, pois o ritmo da referência é copiado. Informe a transcrição do trecho
              (recomendado: evita o passo lento de transcrição automática e melhora a clonagem). Teste e, se gostar, salve a voz.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" accept="audio/*" disabled={!!cloneBusy || !serviceUp}
                onChange={e => { setCloneFile(e.target.files?.[0] || null); setCloneMsg(null); setCloneError(null) }}
                className="text-[11px] file:mr-2 file:rounded file:border-0 file:bg-muted file:px-2 file:py-1 file:text-foreground" />
              <button onClick={uploadClone} disabled={!cloneFile || !!cloneBusy || !serviceUp || !!cloneStaged}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground neu-relief px-3 py-1.5 text-[12px] font-medium disabled:opacity-40">
                {cloneBusy === 'upload' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiUploadCloud className="w-3.5 h-3.5" />} Enviar amostra
              </button>
            </div>
            {cloneStaged && (
              <div className="flex flex-col gap-2 bg-card border border-border neu-card rounded-lg p-2">
                <div className="flex items-center gap-2 text-[11px] text-foreground"><span className="font-semibold">Referência:</span><span className="truncate">{cloneStaged}</span></div>
                <input value={cloneRefText} onChange={e => setCloneRefText(e.target.value)} disabled={!!cloneBusy}
                  placeholder="Transcrição do áudio de referência (recomendado)"
                  className="w-full rounded border border-border bg-background px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary" />
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={testClone} disabled={!!cloneBusy} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted disabled:opacity-40">
                    {cloneBusy === 'test' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlay className="w-3.5 h-3.5" />} Testar clonagem
                  </button>
                  <button onClick={discardClone} disabled={!!cloneBusy} className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 text-red-600 dark:text-red-400 px-3 py-1.5 text-[12px] font-medium hover:bg-red-500/10 disabled:opacity-40">
                    {cloneBusy === 'discard' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiTrash2 className="w-3.5 h-3.5" />} {savedThisOne ? 'Fechar' : 'Descartar'}
                  </button>
                </div>
                {cloneAudioUrl && <audio controls autoPlay src={cloneAudioUrl} className="w-full h-9" />}
                <div className="flex items-center gap-2 flex-wrap border-t border-border pt-2">
                  <input value={saveLabel} onChange={e => setSaveLabel(e.target.value)} disabled={!!cloneBusy}
                    placeholder="Nome da voz (ex.: Jú Atendimento)"
                    className="flex-1 min-w-[160px] rounded border border-border bg-background px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary" />
                  <button onClick={saveVoice} disabled={!!cloneBusy || savedThisOne}
                    title="Salva a referência + transcrição como voz reutilizável"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-[12px] font-medium hover:bg-emerald-700 disabled:opacity-40">
                    {cloneBusy === 'save' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSave className="w-3.5 h-3.5" />} {savedThisOne ? 'Salva' : 'Salvar voz'}
                  </button>
                </div>
              </div>
            )}
            {cloneMsg && <div className="text-[11px] text-emerald-600 dark:text-emerald-400">{cloneMsg}</div>}
            {cloneError && <div className="text-[11px] text-red-600 dark:text-red-400">{cloneError}</div>}
          </div>
        )}
      </div>

      {/* API pública (tts.twms.com.br) — doc + chave Bearer */}
      <div className="border border-border neu-card rounded-lg overflow-hidden">
        <button onClick={() => setShowApi(s => !s)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted/60 transition-colors">
          {showApi ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
          <FiZap className="w-3.5 h-3.5 text-primary" /> API pública (tts.twms.com.br)
        </button>
        {showApi && (
          <div className="px-3 pb-3 flex flex-col gap-3 text-[12px] text-foreground">
            <p className="text-[11px] text-muted-foreground">
              A mesma engine deste painel, exposta em <span className="font-mono">https://tts.twms.com.br</span> com TLS.
              Autenticação por <span className="font-medium text-foreground">Bearer token</span> (gere/rotacione abaixo). Rate-limit de 30 req/min.
            </p>

            {/* Chave Bearer */}
            <div className="rounded-lg border border-border bg-card neu-card p-2.5 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Chave Bearer</span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setRevealKey(v => !v)} className="text-[11px] text-primary hover:underline">{revealKey ? 'ocultar' : 'mostrar'}</button>
                  <button onClick={copyKey} disabled={!apiKey} title="Copiar" className="p-1 rounded hover:bg-muted disabled:opacity-40">{copied ? <FiCheck className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}</button>
                  <button onClick={rotateKey} disabled={rotating} className="inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground neu-relief px-2.5 py-1 text-[11px] font-medium hover:opacity-90 disabled:opacity-40">
                    {rotating ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiRefreshCw className="w-3 h-3" />} Gerar nova
                  </button>
                </div>
              </div>
              <code className="block w-full break-all rounded neu-pit !text-slate-200 px-2 py-1.5 text-[11px] font-mono">
                {apiKey ? (revealKey ? apiKey : '•'.repeat(40)) : (apiKeyError ? '—' : 'carregando…')}
              </code>
              {apiKeyError && <span className="text-[11px] text-red-600 dark:text-red-400">{apiKeyError}</span>}
              <span className="text-[10px] text-muted-foreground">Gerar nova invalida a anterior na hora — atualize quem consome a API. Guarde com cuidado: dá acesso à síntese de voz.</span>
            </div>

            {/* Endpoints */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Endpoints</span>
              <ul className="flex flex-col gap-1 text-[11px] font-mono">
                <li><span className="text-emerald-600 dark:text-emerald-400">POST</span> /generate — texto→áudio (voz automática / voice design)</li>
                <li><span className="text-emerald-600 dark:text-emerald-400">POST</span> /clone — texto→áudio com voz de referência salva</li>
                <li><span className="text-sky-600 dark:text-sky-400">GET</span> /info — modelo e modos</li>
                <li><span className="text-sky-600 dark:text-sky-400">GET</span> /healthz — saúde (aberto, sem token)</li>
              </ul>
            </div>

            {/* curl exemplo */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exemplo (curl)</span>
              <pre className="w-full overflow-x-auto rounded neu-pit !text-slate-200 px-2 py-1.5 text-[10.5px] font-mono leading-relaxed">{`curl -X POST https://tts.twms.com.br/generate \\
  -H "Authorization: Bearer ${revealKey && apiKey ? apiKey : '<SUA_CHAVE>'}" \\
  -H "Content-Type: application/json" \\
  -d '{"text":"Olá do TWMS","num_step":32,"speed":1.0}' \\
  --output fala.wav`}</pre>
              <p className="text-[10px] text-muted-foreground">
                Corpo: <span className="font-mono">text</span> (obrigatório), <span className="font-mono">instruct</span> (voice design, ex.: "female, low pitch"),
                <span className="font-mono"> num_step</span> (16–32), <span className="font-mono">speed</span> (&lt;1 = mais devagar), <span className="font-mono">duration</span> (s, sobrepõe speed).
                Resposta: <span className="font-mono">audio/wav</span> 24 kHz.
              </p>
            </div>
          </div>
        )}
      </div>

      </div>{/* ── fim coluna direita ── */}
      </div>{/* ── fim grid 2 colunas ── */}
    </div>
  )
}
